/**
 * `<SettingsRolesRoute>` — `/w/:slug/roles` (B3).
 *
 * Lists the workspace's built-in roles (read-only) and custom roles, and lets
 * an org manager create / edit / delete custom roles via a permission-checkbox
 * builder. Deleting a role reverts its members to the built-in `member` role
 * (the count is surfaced in the success toast).
 */

import { useAuth, useWorkspace } from "@cometchat-ai/auth/client";
import {
  flagEnabled,
  HELPDESK_ROLE_CEILING,
  isHelpdeskBuiltinRole,
} from "@cometchat-ai/auth/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Pencil, Plus, ShieldCheck, Trash2, UserCog, X } from "../components/icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorState } from "../components/error-state";
import { PermissionCountPill } from "../components/permission-count-pill";
import { formatApiError } from "../lib/format-error";
import { FINAL_FONT } from "../layouts/final-polish";
import { PageShell } from "../components/page-shell";
import { SettingsCard } from "../components/settings-card";
import {
  type CustomRole,
  type QueryPerm,
  type WorkflowPerm,
  createRole,
  deleteRole,
  getRoleQueryPerms,
  getRoleWorkflowPerms,
  listPermissionCatalogue,
  listRoles,
  listWorkspaceQueries,
  listWorkspaceWorkflows,
  resourcePermsKeys,
  rolesKeys,
  setRoleQueryPerms,
  setRoleWorkflowPerms,
  updateRole,
} from "../settings/api";
import { ResourceMatrix } from "../settings/resource-matrix";
import { PermissionChecklist } from "../settings/permission-checklist";

const INPUT_CLASS = "h-9 rounded-lg text-[13px] placeholder:text-[color:var(--color-fg-subtle)]";

/** Attio-style list row used for built-in + custom roles. */
const ROW_CLASS =
  "flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-[13px]";

/** Tier tag shown on every role row (built-in + custom) so admins can tell
 * workspace roles from desk roles — mirrors the Members page. */
function ScopeTag({ scope }: { scope: "workspace" | "helpdesk" }) {
  const helpdesk = scope === "helpdesk";
  return (
    <span
      className={
        helpdesk
          ? "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium text-primary [background:color-mix(in_oklch,var(--color-primary)_12%,transparent)]"
          : "inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
      }
    >
      {helpdesk ? "Helpdesk" : "Workspace"}
    </span>
  );
}

/** Prettify a built-in role KEY (`helpdesk_member`) into a display label
 * ("Helpdesk Member"). The roles API returns built-ins by their raw snake_case
 * key; custom roles carry an author-given name and are shown as-is. */
function builtinRoleLabel(key: string): string {
  return key
    .split("_")
    .map((w) => (w === "kb" ? "KB" : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

export function SettingsRolesRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<CustomRole | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomRole | null>(null);

  const rolesQuery = useQuery({ queryKey: rolesKeys.list(slug), queryFn: () => listRoles(slug) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: rolesKeys.list(slug) });

  const remove = useMutation({
    mutationFn: (id: string) => deleteRole(slug, id),
    onSuccess: (res) => {
      toast.success(t("roles.deletedToast", { count: res.repointed }));
      setDeleteTarget(null);
      void invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const builtin = rolesQuery.data?.builtin ?? [];
  const custom = rolesQuery.data?.custom ?? [];

  const customTitle =
    custom.length === 1
      ? t("roles.customTitleOne")
      : t("roles.customTitleOther", { count: custom.length });

  return (
    <PageShell
      className="p-0"
      data-testid="settings-roles"
      description={t("roles.subtitle")}
      actions={
        <Button
          onClick={() => setEditing("new")}
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          data-testid="role-new"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t("roles.new")}
        </Button>
      }
    >
      <div className="mx-auto w-full max-w-6xl space-y-5 px-6 py-6">
        {rolesQuery.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner aria-label={t("roles.builtinTitle")} />
          </div>
        ) : rolesQuery.error ? (
          <ErrorState message={t("roles.loadError")} onRetry={rolesQuery.refetch} />
        ) : (
          <>
            <SettingsCard icon={ShieldCheck} title={t("roles.builtinTitle")}>
              <ul className="space-y-2">
                {builtin.map((role) => (
                  <li key={role.name} className={ROW_CLASS}>
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium text-foreground">
                        {builtinRoleLabel(role.name)}
                      </span>
                      <ScopeTag
                        scope={isHelpdeskBuiltinRole(role.name) ? "helpdesk" : "workspace"}
                      />
                    </div>
                    <PermissionCountPill
                      label={t("roles.permissionCount", { count: role.permissions.length })}
                      permissions={role.permissions}
                    />
                  </li>
                ))}
              </ul>
            </SettingsCard>

            <SettingsCard icon={UserCog} title={customTitle}>
              {custom.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-8 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                    <UserCog className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </span>
                  <p className="text-sm font-medium text-foreground">{t("roles.empty")}</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {custom.map((role) => (
                    <li key={role.id} className={ROW_CLASS} data-testid={`role-row-${role.id}`}>
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium text-foreground">{role.name}</span>
                        <ScopeTag scope={role.scope === "helpdesk" ? "helpdesk" : "workspace"} />
                        <PermissionCountPill
                          label={t("roles.permissionCount", { count: role.permissions.length })}
                          permissions={role.permissions}
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1.5 rounded-md px-2 text-[13px]"
                          onClick={() => setEditing(role)}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          {t("roles.edit")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1.5 rounded-md px-2 text-[13px] text-destructive hover:bg-[color:color-mix(in_oklch,var(--color-danger)_10%,transparent)] hover:text-destructive"
                          onClick={() => setDeleteTarget(role)}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          {t("roles.delete")}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SettingsCard>
          </>
        )}
      </div>

      {editing ? (
        <RoleBuilderDialog
          slug={slug}
          role={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("roles.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("roles.deleteBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
              disabled={remove.isPending}
            >
              {t("roles.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

// Workflow/query ACTION verbs are authored in the dedicated sections below
// (All vs Specific), not as generic catalogue checkboxes — so hide them from
// the general list to avoid two places setting the same thing.
const WF_MATRIX = [
  { key: "run", slug: "workflow:run", label: "Run" },
  { key: "signal", slug: "workflow:signal", label: "Respond" },
  { key: "cancel", slug: "workflow:cancel", label: "Cancel" },
  { key: "publish", slug: "workflow:publish", label: "Publish" },
] as const;
const Q_MATRIX = [
  { key: "run", slug: "query:run", label: "Run" },
  { key: "edit", slug: "query:update", label: "Edit" },
] as const;
const HIDDEN_SLUGS = new Set<string>([
  ...WF_MATRIX.map((v) => v.slug),
  ...Q_MATRIX.map((v) => v.slug),
]);

type Scope = "all" | "specific";
type WfCols = { run: boolean; signal: boolean; cancel: boolean; publish: boolean };
type QCols = { run: boolean; edit: boolean };
const EMPTY_WF: WfCols = { run: false, signal: false, cancel: false, publish: false };
const EMPTY_Q: QCols = { run: false, edit: false };

function RoleBuilderDialog({
  slug,
  role,
  onClose,
  onSaved,
}: {
  slug: string;
  role: CustomRole | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation("workspace");
  const auth = useAuth();
  const [name, setName] = useState(role?.name ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((role?.permissions ?? []).filter((p) => !HIDDEN_SLUGS.has(p))),
  );
  const [wfScope, setWfScope] = useState<Scope>(
    role && WF_MATRIX.some((v) => role.permissions.includes(v.slug)) ? "all" : "specific",
  );
  const [qScope, setQScope] = useState<Scope>(
    role && Q_MATRIX.some((v) => role.permissions.includes(v.slug)) ? "all" : "specific",
  );
  const [wfBlanket, setWfBlanket] = useState<Set<string>>(
    () => new Set(WF_MATRIX.filter((v) => role?.permissions.includes(v.slug)).map((v) => v.key)),
  );
  const [qBlanket, setQBlanket] = useState<Set<string>>(
    () => new Set(Q_MATRIX.filter((v) => role?.permissions.includes(v.slug)).map((v) => v.key)),
  );
  const [wfSpecific, setWfSpecific] = useState<Map<string, WfCols>>(new Map());
  const [qSpecific, setQSpecific] = useState<Map<string, QCols>>(new Map());
  const [seeded, setSeeded] = useState(false);

  const catalogue = useQuery({
    queryKey: rolesKeys.catalogue(slug),
    queryFn: () => listPermissionCatalogue(slug),
  });
  const [wfListQ, qListQ, wfPermQ, qPermQ] = useQueries({
    queries: [
      { queryKey: resourcePermsKeys.workflows(slug), queryFn: listWorkspaceWorkflows },
      { queryKey: resourcePermsKeys.queries(slug), queryFn: listWorkspaceQueries },
      {
        queryKey: resourcePermsKeys.roleWorkflow(slug, role?.id ?? "new"),
        queryFn: () => getRoleWorkflowPerms(slug, role!.id),
        enabled: !!role,
      },
      {
        queryKey: resourcePermsKeys.roleQuery(slug, role?.id ?? "new"),
        queryFn: () => getRoleQueryPerms(slug, role!.id),
        enabled: !!role,
      },
    ],
  });

  const resourcesLoading = wfListQ.isPending || qListQ.isPending;
  const permsSettled = !role || (!wfPermQ.isPending && !qPermQ.isPending);

  // Seed the per-resource matrices once the role's grants have loaded.
  if (role && !seeded && permsSettled) {
    const wf = new Map<string, WfCols>();
    for (const g of wfPermQ.data ?? [])
      wf.set(g.workflowId, {
        run: g.run,
        signal: g.signal,
        cancel: g.cancel,
        publish: g.publish,
      });
    const q = new Map<string, QCols>();
    for (const g of qPermQ.data ?? []) q.set(g.storedQueryId, { run: g.run, edit: g.edit });
    setWfSpecific(wf);
    setQSpecific(q);
    setSeeded(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const permissions = [...selected];
      if (wfScope === "all")
        for (const v of WF_MATRIX) if (wfBlanket.has(v.key)) permissions.push(v.slug);
      if (qScope === "all")
        for (const v of Q_MATRIX) if (qBlanket.has(v.key)) permissions.push(v.slug);
      const saved = role
        ? await updateRole(slug, role.id, { name, permissions })
        : await createRole(slug, { name, permissions });
      const roleKey = saved.id;
      const wfEntries: WorkflowPerm[] =
        wfScope === "specific"
          ? [...wfSpecific.entries()].map(([workflowId, f]) => ({
              workflowId,
              ...f,
              manage: false,
            }))
          : [];
      const qEntries: QueryPerm[] =
        qScope === "specific"
          ? [...qSpecific.entries()].map(([storedQueryId, f]) => ({ storedQueryId, ...f }))
          : [];
      await Promise.all([
        setRoleWorkflowPerms(slug, roleKey, wfEntries),
        setRoleQueryPerms(slug, roleKey, qEntries),
      ]);
    },
    onSuccess: () => {
      toast.success(role ? t("roles.updatedToast") : t("roles.createdToast"));
      onSaved();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  // A HELPDESK-scoped role can only ever grant slugs inside the desk ceiling
  // (the backend rejects the rest). So when editing one from this workspace
  // page, constrain the picker to that ceiling — the forbidden slugs are never
  // shown, which is why they can't be selected and the save can't fail on
  // ceiling grounds. Scope is immutable, so this only applies on edit; new
  // roles created here are always workspace-scoped (full catalogue).
  const isHelpdeskRole = role?.scope === "helpdesk";
  const grantable = useMemo(
    () => (isHelpdeskRole ? new Set<string>(HELPDESK_ROLE_CEILING) : null),
    [isHelpdeskRole],
  );
  // Desk ceiling allows workflow run/signal/cancel (no publish) and NO query:*.
  const wfMatrix = isHelpdeskRole
    ? WF_MATRIX.filter((v) => v.slug !== "workflow:publish")
    : WF_MATRIX;
  // Workflows surface is flag-gated (default OFF): hide the workflow permission
  // rows + the Workflow access section until the infra ships. Hidden only — a
  // role that already holds workflow grants keeps them (save is untouched).
  const workflowsEnabled = flagEnabled(auth, "surface.workflows");

  const groups = (catalogue.data?.groups ?? [])
    .map((g) => ({
      ...g,
      permissions: g.permissions.filter(
        (p) =>
          !HIDDEN_SLUGS.has(p) &&
          (!grantable || grantable.has(p)) &&
          (workflowsEnabled || !p.startsWith("workflow:")),
      ),
    }))
    .filter((g) => g.permissions.length > 0);
  const canSave = useMemo(() => name.trim().length > 0 && selected.size > 0, [name, selected]);

  const toggle = (perm: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(perm) ? next.delete(perm) : next.add(perm);
      return next;
    });
  const toggleBlanket = (set: Set<string>, setFn: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setFn(next);
  };
  const toggleWfSpecific = (id: string, col: string) =>
    setWfSpecific((prev) => {
      const next = new Map(prev);
      const cur = next.get(id) ?? { ...EMPTY_WF };
      next.set(id, { ...cur, [col]: !cur[col as keyof WfCols] });
      return next;
    });
  const toggleQSpecific = (id: string, col: string) =>
    setQSpecific((prev) => {
      const next = new Map(prev);
      const cur = next.get(id) ?? { ...EMPTY_Q };
      next.set(id, { ...cur, [col]: !cur[col as keyof QCols] });
      return next;
    });

  const workflows = wfListQ.data ?? [];
  const queries = qListQ.data ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        style={{ fontFamily: FINAL_FONT }}
        className="flex max-h-[85vh] max-w-xl flex-col gap-0 rounded-2xl p-0 sm:rounded-2xl"
      >
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-medium tracking-[0.01em]">
            {role ? t("roles.edit") : t("roles.create")}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {t("roles.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="role-name" className="text-[13px] font-medium text-foreground">
              {t("roles.nameLabel")}
            </Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("roles.namePlaceholder")}
              className={INPUT_CLASS}
              style={{ fontFamily: "inherit" }}
              data-testid="role-name-input"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-[13px] font-medium text-foreground">
              {t("roles.permissionsLabel")}
            </Label>
            {isHelpdeskRole ? (
              <p className="text-xs text-muted-foreground">{t("roles.helpdeskScopeNote")}</p>
            ) : null}
            {catalogue.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner aria-label={t("roles.permissionsLabel")} />
              </div>
            ) : (
              <PermissionChecklist groups={groups} selected={selected} onToggle={toggle} />
            )}
          </div>

          {workflowsEnabled ? (
            <AccessSection
              title={t("roles.accessWorkflows")}
              scope={wfScope}
              onScope={setWfScope}
              allLabel={t("roles.accessAllWorkflows")}
              specificLabel={t("roles.accessSpecific")}
              verbs={wfMatrix}
              blanket={wfBlanket}
              onToggleBlanket={(k) => toggleBlanket(wfBlanket, setWfBlanket, k)}
              loading={resourcesLoading || !permsSettled}
              rows={workflows}
              emptyLabel={t("roles.accessNoWorkflows")}
              isChecked={(id, col) => (wfSpecific.get(id) ?? EMPTY_WF)[col as keyof WfCols]}
              onToggleSpecific={toggleWfSpecific}
            />
          ) : null}
          {/* Stored queries are a builder/analyst surface — the desk holds no
              query:* slugs, so the Query access section is hidden for a
              helpdesk role (it would only ever reject on save). */}
          {isHelpdeskRole ? null : (
            <AccessSection
              title={t("roles.accessQueries")}
              scope={qScope}
              onScope={setQScope}
              allLabel={t("roles.accessAllQueries")}
              specificLabel={t("roles.accessSpecific")}
              verbs={Q_MATRIX}
              blanket={qBlanket}
              onToggleBlanket={(k) => toggleBlanket(qBlanket, setQBlanket, k)}
              loading={resourcesLoading || !permsSettled}
              rows={queries}
              emptyLabel={t("roles.accessNoQueries")}
              isChecked={(id, col) => (qSpecific.get(id) ?? EMPTY_Q)[col as keyof QCols]}
              onToggleSpecific={toggleQSpecific}
            />
          )}
        </div>

        <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
          <Button
            variant="secondary"
            size="sm"
            className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
            data-testid="role-save"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="h-4 w-4" aria-hidden="true" />
            )}
            {save.isPending ? t("roles.saving") : t("roles.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A workflow/query access section: "All" (blanket verbs) vs "Specific" (matrix). */
function AccessSection({
  title,
  scope,
  onScope,
  allLabel,
  specificLabel,
  verbs,
  blanket,
  onToggleBlanket,
  loading,
  rows,
  emptyLabel,
  isChecked,
  onToggleSpecific,
}: {
  title: string;
  scope: Scope;
  onScope: (s: Scope) => void;
  allLabel: string;
  specificLabel: string;
  verbs: ReadonlyArray<{ key: string; slug: string; label: string }>;
  blanket: Set<string>;
  onToggleBlanket: (key: string) => void;
  loading: boolean;
  rows: Array<{ id: string; name: string }>;
  emptyLabel: string;
  isChecked: (id: string, col: string) => boolean;
  onToggleSpecific: (id: string, col: string) => void;
}) {
  const seg = (value: Scope, label: string) => (
    <button
      type="button"
      onClick={() => onScope(value)}
      className={`h-7 rounded-md px-3 text-[12px] font-medium transition-colors ${
        scope === value
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
      data-testid={`scope-${title}-${value}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-[13px] font-medium text-foreground">{title}</Label>
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5">
          {seg("all", allLabel)}
          {seg("specific", specificLabel)}
        </div>
      </div>
      {scope === "all" ? (
        <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-border px-4 py-3">
          {verbs.map((v) => (
            <label key={v.key} className="flex cursor-pointer items-center gap-2 text-[13px]">
              <Checkbox
                checked={blanket.has(v.key)}
                onCheckedChange={() => onToggleBlanket(v.key)}
                className="h-4 w-4 rounded-[5px]"
              />
              <span className="text-foreground">{v.label}</span>
            </label>
          ))}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-6">
          <Spinner aria-label={title} />
        </div>
      ) : (
        <ResourceMatrix
          rows={rows}
          cols={verbs.map((v) => ({ key: v.key, label: v.label }))}
          emptyLabel={emptyLabel}
          isChecked={isChecked}
          onToggle={onToggleSpecific}
        />
      )}
    </div>
  );
}
